import React from 'react';
import { User, FileText, CheckCircle, Clock, TrendingUp, Eye, Percent } from 'lucide-react';

interface EditorWorkloadCardProps {
  editor: {
    id: string;
    name: string;
    displayName: string;
    activeRequests: number;
    completedRequests: number;
    totalRequests: number;
    totalAssignedCreatives?: number;
    totalCompletedCreatives?: number;
    totalUploadedCreatives?: number;
    completionRate?: number;
    loadPercentage: number;
    status: string;
    maxConcurrentRequests: number | null;
    avgCompletionTimeHours: number;
    isAvailable: boolean;
  };
  onViewDetails: () => void;
  onRefresh: () => void;
}

export function EditorWorkloadCard({ editor, onViewDetails }: EditorWorkloadCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <User className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {editor.displayName || editor.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{editor.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Creatives */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Assigned Creatives</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {editor.totalAssignedCreatives ?? editor.activeRequests}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <FileText size={16} />
            <span>Assigned Creatives</span>
          </div>
          <span className="font-semibold text-purple-600">{editor.totalAssignedCreatives ?? editor.activeRequests}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <CheckCircle size={16} />
            <span>Completed Creatives</span>
          </div>
          <span className="font-semibold text-green-600">{editor.totalCompletedCreatives ?? editor.totalUploadedCreatives ?? editor.completedRequests}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Percent size={16} />
            <span>Completion Rate</span>
          </div>
          <span className={`font-semibold ${
            (editor.completionRate ?? 0) >= 80 ? 'text-green-600' :
            (editor.completionRate ?? 0) >= 50 ? 'text-yellow-600' : 'text-gray-500'
          }`}>{editor.completionRate ?? 0}%</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Clock size={16} />
            <span>Active Requests</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white">
            {editor.activeRequests}
            {(editor.totalAssignedCreatives ?? 0) > 0 && (
              <span className="ml-1 text-xs font-normal text-gray-500">· {editor.totalAssignedCreatives} creatives</span>
            )}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <TrendingUp size={16} />
            <span>Total Requests</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white">{editor.totalRequests}</span>
        </div>
      </div>

      {/* Action Button */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onViewDetails}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Eye size={16} />
          View Details
        </button>
      </div>
    </div>
  );
}
